/**
 * Comma & Co gift shop: small behaviours shared by the new sections.
 *
 * 1. Sections rise in gently as they reach the screen (.cc-reveal). Unused since 2026-09-29: never put it on
 *    anything holding products or buttons (it left the season and price bands blank while scrolling). Anything
 *    already on screen when the script runs is shown at once, so nothing
 *    flickers; with reduced motion nothing moves at all.
 * 2. The gift finder: three selects make a sentence, the button goes to the
 *    matching collection with a price filter, and the last choice is
 *    remembered on this device (cc:finder) so a return visit opens on it.
 *    The sentence changes by itself at every width (2b).
 * 3. "Your favourites": a strip that appears only for a visitor who has saved
 *    something (cc:favs, written by cc-favourites.js).
 * 4. Countdowns on seasonal bands ([data-cc-days-to="MM-DD"]).
 * 5. Desktop menu dropdowns: each one placed under its own link.
 *
 * Nothing here is needed to use the shop; every link works without it.
 */
(() => {
  'use strict';

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. reveal ---------- */
  const reveal = () => {
    const items = document.querySelectorAll('.cc-reveal');
    if (!items.length || reduced || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    const vh = innerHeight;
    items.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) return; // on screen already: leave it alone
      el.classList.add('cc-reveal--armed');
      io.observe(el);
    });
    document.documentElement.classList.add('js-reveal');
  };

  /* ---------- 2. gift finder ---------- */
  const finder = () => {
    document.querySelectorAll('[data-cc-finder]').forEach((form) => {
      const who = form.querySelector('[name="who"]');
      const loves = form.querySelector('[name="loves"]');
      const under = form.querySelector('[name="under"]');
      const go = form.querySelector('[data-cc-finder-go]');
      if (!loves || !under || !go) return;

      // Each select sizes to its chosen words, so the sentence reads as one line
      // of type rather than three boxes.
      const measure = document.createElement('span');
      measure.className = 'cc-finder-measure';
      measure.setAttribute('aria-hidden', 'true');
      form.appendChild(measure);
      const fit = (sel) => {
        if (!sel) return;
        measure.textContent = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text : '';
        sel.style.width = Math.ceil(measure.getBoundingClientRect().width) + 'px';
      };

      // "Gift for" (custom.gift_for): my mum / my dad / a teenager narrow the results, but only in a
      // collection with at least 6 products left for that person (counts from snippets/cc-giftfor-counts,
      // written by catalogue-filters/recipient.js). Anyone else sees the whole collection.
      let giftFor = {};
      try { giftFor = JSON.parse(form.dataset.ccGiftfor || '{}'); } catch (e) { giftFor = {}; }
      const recipient = () => {
        const w = who && who.options[who.selectedIndex] ? who.options[who.selectedIndex].text.toLowerCase() : '';
        if (/teen/.test(w)) return 'For teens';
        if (/\b(mum|mother|mom|wife|girlfriend|sister|nan|nanna|gran|grandma|aunt|auntie|her)\b/.test(w)) return 'For her';
        if (/\b(dad|father|husband|boyfriend|brother|grandad|grandpa|uncle|him)\b/.test(w)) return 'For him';
        return '';
      };
      const url = () => {
        const opt = loves.options[loves.selectedIndex];
        const base = (opt && opt.dataset.url) || '/collections/perfect-for-gifting';
        const handle = base.split('?')[0].split('/').pop();
        const r = recipient();
        const params = [];
        if (r && ((giftFor[handle] || {})[r] || 0) >= 6) params.push(`filter.p.m.custom.gift_for=${encodeURIComponent(r)}`);
        if (under.value) params.push(`filter.v.price.lte=${encodeURIComponent(under.value)}`, 'sort_by=best-selling');
        if (!params.length) return base;
        return `${base}${base.indexOf('?') === -1 ? '?' : '&'}${params.join('&')}`;
      };
      const words = (sel) => (sel && sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text.trim() : '');
      const sync = (save) => {
        [who, loves, under].forEach(fit);
        go.href = url();
        go.setAttribute('aria-label', `Show gifts: ${who ? `for ${words(who)}, ` : ''}who loves ${words(loves)}, under ${words(under)}`);
        if (!save) return;
        try {
          localStorage.setItem('cc:finder', JSON.stringify({ who: who && who.value, loves: loves.value, under: under.value }));
        } catch (e) { /* private mode: nothing remembered */ }
      };

      const auto = form.hasAttribute('data-cc-auto');
      // Put back the last sentence this device made (the changing sentence
      // picks its own, starting on the first scene).
      if (!auto) {
        try {
          const last = JSON.parse(localStorage.getItem('cc:finder') || 'null');
          if (last) {
            [[who, last.who], [loves, last.loves], [under, last.under]].forEach(([sel, v]) => {
              if (sel && v && [...sel.options].some((o) => o.value === v)) sel.value = v;
            });
          }
        } catch (e) {}
      }

      [who, loves, under].forEach((sel) => sel && sel.addEventListener('change', () => sync(true)));
      // Fonts arrive after first paint; measure again once they have.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => [who, loves, under].forEach(fit));
      addEventListener('resize', () => [who, loves, under].forEach(fit), { passive: true });
      sync(false);
      if (auto) autoFinder(form, { who, loves, under }, () => sync(false));
    });
  };

  /* ---------- 2b. the changing sentence (every width since 2026-09-27) ---------- */
  // The sentence moves through the section's scenes ("who | loves | budget",
  // one per line) by itself, starting on the first. Each word that changes
  // rises out as the new one rises in, and its pen line is drawn again; "Show
  // gifts" always opens the scene on screen. The selects stay the record of
  // what is showing. It holds still while a pointer, a finger or focus is on
  // the finder, pauses with the ring button, waits in a background tab, and
  // with reduced motion only moves once the visitor presses play.
  const autoFinder = (form, s, onScene) => {
    const norm = (t) => String(t || '').trim().toLowerCase().replace(/^£/, '');
    const find = (sel, text) => (sel ? [...sel.options].find((o) => norm(o.text) === norm(text) || norm(o.value) === norm(text)) || null : null);
    const scenes = (form.dataset.ccScenes || '').split(/\r?\n/).map((line) => {
      const [w, l, u] = line.split('|');
      if (u === undefined) return null;
      const sc = { who: find(s.who, w), loves: find(s.loves, l), under: find(s.under, u) };
      return sc.loves && sc.under && (sc.who || !s.who) ? sc : null;
    }).filter(Boolean);
    if (scenes.length < 2) return;

    const slots = ['who', 'loves', 'under'].filter((k) => s[k]).map((k) => {
      const pen = s[k].closest('.cc-pen');
      return { key: k, sel: s[k], pen, rot: pen && pen.querySelector('.cc-rot') };
    }).filter((x) => x.rot);
    const copy = form.closest('.cc-ghero-copy') || form.parentElement;
    const toggle = copy.querySelector('[data-cc-rot-toggle]');
    const secs = Math.max(2.5, Number(form.dataset.ccSecs) || 4);

    const swap = (slot, text, delay) => {
      const rot = slot.rot;
      const ghost = rot.querySelector('.cc-rot-ghost');
      if (!ghost || ghost.textContent === text) return;
      setTimeout(() => {
        const cur = rot.querySelector('.cc-rot-w:not(.is-above)');
        // Glide the slot from the old word's width to the new one's.
        const from = rot.getBoundingClientRect().width;
        ghost.textContent = text;
        rot.style.width = '';
        const to = rot.getBoundingClientRect().width;
        rot.style.width = `${from}px`;
        rot.getBoundingClientRect();
        rot.style.width = `${to}px`;
        const next = document.createElement('span');
        next.className = 'cc-rot-w is-below';
        next.textContent = text;
        rot.appendChild(next);
        next.getBoundingClientRect();
        if (cur) cur.classList.add('is-above');
        next.classList.remove('is-below');
        slot.pen.classList.remove('is-drawing');
        void slot.pen.offsetWidth;
        slot.pen.classList.add('is-drawing');
        setTimeout(() => { if (cur) cur.remove(); rot.style.width = ''; }, 700);
      }, delay);
    };
    const apply = (sc) => {
      let n = 0;
      slots.forEach((slot) => {
        const opt = sc[slot.key];
        if (!opt || slot.sel.value === opt.value) return;
        slot.sel.value = opt.value;
        swap(slot, opt.text.trim(), (n++) * 140);
      });
      onScene();
    };

    let i = scenes.findIndex((sc) => slots.every((slot) => !sc[slot.key] || sc[slot.key].value === slot.sel.value));
    if (i < 0) { i = 0; apply(scenes[0]); }
    let timer = 0;
    let paused = reduced;
    let held = false;
    const ring = (on) => {
      if (!toggle) return;
      toggle.classList.remove('is-running');
      if (on) { void toggle.offsetWidth; toggle.classList.add('is-running'); }
    };
    const run = () => {
      clearTimeout(timer);
      const still = paused || held || document.hidden;
      ring(!still);
      if (still) return;
      timer = setTimeout(() => { i = (i + 1) % scenes.length; apply(scenes[i]); run(); }, secs * 1000);
    };

    // Keep the sentence as tall as its tallest scene, so a scene that wraps onto an extra line never pushes the
    // page below it down (the owner can add longer words in the editor at any time).
    const sentence = form.querySelector('.cc-finder-sentence');
    const reserve = () => {
      if (!sentence) return;
      sentence.style.minHeight = '';
      const ghosts = slots.map((sl) => sl.rot.querySelector('.cc-rot-ghost'));
      const keep = ghosts.map((g) => g.textContent);
      let max = 0;
      scenes.forEach((sc) => {
        slots.forEach((sl, n) => { if (sc[sl.key] && ghosts[n]) ghosts[n].textContent = sc[sl.key].text.trim(); sl.rot.style.width = ''; });
        max = Math.max(max, sentence.getBoundingClientRect().height);
      });
      ghosts.forEach((g, n) => { if (g) g.textContent = keep[n]; });
      sentence.style.minHeight = `${Math.ceil(max)}px`;
    };
    reserve();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(reserve);
    let rt = 0;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(reserve, 200); }, { passive: true });

    const paint = () => { if (toggle) toggle.setAttribute('aria-pressed', String(paused)); };
    form.addEventListener('pointerenter', () => { held = true; run(); });
    form.addEventListener('pointerleave', () => { held = false; run(); });
    form.addEventListener('focusin', () => { held = true; run(); });
    form.addEventListener('focusout', (e) => { if (!form.contains(e.relatedTarget)) { held = false; run(); } });
    document.addEventListener('visibilitychange', run);
    // Every word is also a choice (the select sits invisibly on top of it). A visitor who picks one has made
    // their own sentence: show their word and stop changing it. The ring's play button starts it again.
    slots.forEach((slot) => slot.sel.addEventListener('change', () => {
      const opt = slot.sel.options[slot.sel.selectedIndex];
      if (opt) swap(slot, opt.text.trim(), 0);
      paused = true; paint(); run();
    }));
    if (toggle) {
      toggle.hidden = false;
      toggle.style.setProperty('--cc-secs', `${secs}s`);
      toggle.addEventListener('click', () => { paused = !paused; paint(); run(); });
      paint();
    }
    run();
  };

  /* ---------- 3. your favourites strip ---------- */
  const favStrip = () => {
    const host = document.querySelector('[data-cc-favstrip]');
    if (!host) return;
    let list = [];
    try { list = JSON.parse(localStorage.getItem('cc:favs') || '[]'); } catch (e) { list = []; }
    list = Array.isArray(list) ? list.filter((x) => x && x.h && !x.gone) : [];
    if (!list.length) return;
    const track = host.querySelector('[data-cc-favstrip-track]');
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    const img = (u) => (!u ? '' : /[?&]width=/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=240') : u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=240');
    const money = (p) => (typeof p === 'number' ? '£' + (p / 100).toFixed(2) : '');
    track.innerHTML = list.slice(0, 12).map((x) => `
      <a class="cc-fstrip-item" href="/products/${encodeURIComponent(x.h)}">
        <span class="cc-fstrip-shot">${x.img ? `<img src="${esc(img(x.img))}" alt="" width="120" height="120" loading="lazy">` : ''}</span>
        <span class="cc-fstrip-t">${esc(x.t)}</span>
        <span class="cc-fstrip-p">${money(x.p)}</span>
      </a>`).join('');
    const n = host.querySelector('[data-cc-favstrip-n]');
    if (n) n.textContent = list.length === 1 ? '1 saved' : `${list.length} saved`;
    host.hidden = false;
  };

  /* ---------- 4. countdowns ---------- */
  const countdowns = () => {
    document.querySelectorAll('[data-cc-days-to]').forEach((el) => {
      const [m, d] = el.dataset.ccDaysTo.split('-').map(Number);
      if (!m || !d) return;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let target = new Date(now.getFullYear(), m - 1, d);
      if (target < today) target = new Date(now.getFullYear() + 1, m - 1, d);
      const days = Math.round((target - today) / 86400000);
      if (days === 0) el.textContent = el.dataset.ccToday || 'It’s today';
      else if (days <= 60) el.textContent = `${days} ${days === 1 ? 'day' : 'days'} to go`;
      else el.textContent = `${Math.round(days / 7)} weeks to go`;
      el.hidden = false;
    });
  };

  /* ---------- 5. desktop menu dropdowns ---------- */
  // Horizon hangs every submenu off the header row, so each is placed here
  // with its first line of text under its link's text (cc-gift.css,
  // "Dropdowns", draws the panel). Placed again on hover, in case the menu
  // has re-rendered or the window changed size.
  const dropdowns = () => {
    const ITEM = '#header-component .menu-list__list-item:not([slot])';
    const place = (li) => {
      const sub = li.querySelector(':scope > .menu-list__submenu');
      const row = sub && sub.offsetParent;
      const title = li.querySelector('.menu-list__link-title');
      if (!row || !title) return;
      const inner = sub.querySelector('.menu-list__submenu-inner');
      const link = sub.querySelector('.mega-menu__link');
      const px = (el, p) => (el ? parseFloat(getComputedStyle(el)[p]) || 0 : 0);
      const indent = px(inner, 'borderLeftWidth') + px(inner, 'paddingLeft') + px(link, 'paddingLeft');
      const rr = row.getBoundingClientRect();
      let x = title.getBoundingClientRect().left + px(title, 'paddingLeft') - rr.left - indent;
      x = Math.max(12, Math.min(x, rr.width - sub.offsetWidth - 12));
      sub.style.setProperty('--cc-dd-left', `${Math.round(x)}px`);
    };
    const all = () => document.querySelectorAll(ITEM).forEach(place);
    all();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(all);
    let raf = 0;
    addEventListener('resize', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(all); }, { passive: true });
    const near = (e) => { const li = e.target.closest && e.target.closest(ITEM); if (li) place(li); };
    document.addEventListener('pointerover', near, { passive: true });
    document.addEventListener('focusin', near);
  };

  /* ---------- 6. photos that are not packshots ---------- */
  // A product shot on white sits on the stone plate with room around it
  // (multiply turns the white into stone). A photo with its own background,
  // such as a wrist or a dark table, would show as a framed square, so it
  // fills the plate instead ("is-photo", cc-gift.css). Once each image has
  // loaded, a 12px copy of it is read: light, even edges mean a packshot.
  const SHOTS = '.cc-card .cc-shot img, product-card .card-gallery img, .cc-dpick-shot img, .cc-fstrip-shot img, .cc-mini-shot img, .cc-chead-plate img';
  const PLATE = '.cc-shot, .card-gallery, .cc-dpick-shot, .cc-fstrip-shot, .cc-mini-shot, .cc-chead-plate';
  const shots = () => {
    const c = document.createElement('canvas');
    c.width = 12; c.height = 12;
    const g = c.getContext('2d', { willReadFrequently: true });
    if (!g) return;
    const EDGE = [[0, 0], [11, 0], [0, 11], [11, 11], [6, 0], [0, 6], [11, 6], [6, 11]];
    const judge = (img) => {
      if (img.dataset.ccShot || !img.complete || !img.naturalWidth) return;
      let kind = 'pack';
      try {
        g.clearRect(0, 0, 12, 12);
        g.drawImage(img, 0, 0, 12, 12);
        const d = g.getImageData(0, 0, 12, 12).data;
        const light = EDGE.filter(([x, y]) => {
          const i = (y * 12 + x) * 4;
          const lo = Math.min(d[i], d[i + 1], d[i + 2]);
          const hi = Math.max(d[i], d[i + 1], d[i + 2]);
          return d[i + 3] < 20 || (lo > 226 && hi - lo < 24);
        }).length;
        if (light < 6) kind = 'photo';
      } catch (e) { kind = 'unknown'; }
      img.dataset.ccShot = kind;
      if (kind === 'photo') { const plate = img.closest(PLATE); if (plate) plate.classList.add('is-photo'); }
    };
    document.querySelectorAll(SHOTS).forEach(judge);
    document.addEventListener('load', (e) => { const t = e.target; if (t && t.tagName === 'IMG' && t.matches(SHOTS)) judge(t); }, true);
  };

  /* ---------- 7. the chat button and the buy buttons (phones) ---------- */
  // Shopify's chat button floats at the bottom left. On a phone it lands on
  // the product page's quantity box and on the bag page's Check out button,
  // so it fades out while those buttons pass through the bottom strip it
  // sits in, and comes back once they have gone by (the sticky bar leaves
  // room for it). The bag drawer is a dialog above the chat, so not needed there.
  const chatClear = () => {
    const buy = [...document.querySelectorAll('.product-details .buy-buttons-block, .cart__ctas')]
      .filter((el) => !el.closest('dialog, cart-drawer-component, #cart-drawer'));
    if (!buy.length || !('IntersectionObserver' in window)) return;
    const phone = matchMedia('(max-width: 749px)');
    let io = null;
    const watch = () => {
      if (io) io.disconnect();
      document.documentElement.classList.remove('cc-chat-clear');
      if (!phone.matches) return;
      const seen = new Map();
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => seen.set(e.target, e.isIntersecting));
        document.documentElement.classList.toggle('cc-chat-clear', [...seen.values()].some(Boolean));
      }, { rootMargin: `-${Math.max(0, innerHeight - 110)}px 0px 0px 0px` });
      buy.forEach((el) => io.observe(el));
    };
    watch();
    phone.addEventListener('change', watch);
    let t = 0;
    addEventListener('resize', () => { clearTimeout(t); t = setTimeout(watch, 200); }, { passive: true });
  };

  /* ---------- 8. "Cookie preferences" in the footer ---------- */
  // Reopens Shopify's cookie banner (privacyBanner.showPreferences, shopify.dev cookie-banner docs) so a
  // visitor can change their choice any time. Without the banner script the link simply opens the policy.
  const cookiePrefs = () => {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('[data-cc-cookie-prefs]');
      if (!a || !window.privacyBanner || typeof window.privacyBanner.showPreferences !== 'function') return;
      e.preventDefault();
      window.privacyBanner.showPreferences();
    });
  };

  // iOS Safari only shows :active (the button press in cc-gift.css) when the page has a touch listener.
  document.addEventListener('touchstart', () => {}, { passive: true });

  const start = () => { finder(); favStrip(); countdowns(); reveal(); dropdowns(); shots(); chatClear(); cookiePrefs(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
